<template>
  <b-card>
    <div class="d-flex flex-wrap align-items-center mb-1">
      <b-form-radio-group
          v-model="chartSlug"
          :options="chartOptions"
          buttons
          button-variant="outline-primary"
          class="mr-2 mb-50"
      />
      <b-form-select
          v-model="year"
          :options="years"
          style="width: 110px;"
          class="mr-1 mb-50"
      />
      <b-form-select
          v-if="!isAnnual"
          v-model="selectedWeek"
          :options="weekOptions"
          style="width: 170px;"
          class="mr-1 mb-50"
      />
      <b-button-group class="mb-50">
        <b-button variant="outline-secondary" :disabled="!edition || !edition.prev_date" @click="goTo(edition.prev_date)">
          <feather-icon icon="ChevronLeftIcon"/>
        </b-button>
        <b-button variant="outline-secondary" :disabled="!edition || !edition.next_date" @click="goTo(edition.next_date)">
          <feather-icon icon="ChevronRightIcon"/>
        </b-button>
      </b-button-group>
      <div v-if="edition" class="ml-auto text-muted mb-50">
        <template v-if="isAnnual">{{ edition.week_start.slice(0, 4) }} год</template>
        <template v-else>{{ edition.week_start }} — {{ edition.week_end }}</template>
      </div>
    </div>

    <b-overlay :show="isLoading" rounded="sm">
      <b-table
          v-if="edition"
          :items="edition.entries"
          :fields="fields"
          small
          hover
          responsive
      >
        <template #cell(position)="{ item }">
          <strong>{{ item.position }}</strong>
        </template>
        <template #cell(move)="{ item }">
          <b-badge v-if="item.is_new" variant="success">NEW</b-badge>
          <b-badge v-else-if="item.is_reentry" variant="info">RE</b-badge>
          <span v-else-if="item.last_week && item.last_week > item.position" class="text-success">↑{{ item.last_week - item.position }}</span>
          <span v-else-if="item.last_week && item.last_week < item.position" class="text-danger">↓{{ item.position - item.last_week }}</span>
          <span v-else class="text-muted">=</span>
        </template>
        <template #cell(plays)="{ item }">
          <span v-if="item.plays !== null">{{ formatNumber(item.plays) }}</span>
          <span v-else class="text-muted">—</span>
        </template>
        <template #cell(song)="{ item }">
          <div class="d-flex align-items-center">
            <b-img
                v-if="item.song.image_url"
                :src="item.song.image_url"
                width="36"
                height="36"
                rounded
                class="mr-50"
            />
            <b-link @click="openHistory(item.song)">
              {{ item.song.artist_name }} — {{ item.song.title }}
            </b-link>
          </div>
        </template>
        <template #cell(link)="{ item }">
          <!-- Полное соответствие: трек найден (для альбомов — артист) -->
          <template v-if="item.song.track || (item.song.kind === 'albums' && item.song.artist)">
            <feather-icon icon="CheckIcon" class="text-success mr-25"/>
            <b-link
                v-if="item.song.track"
                :to="{ name: 'track-detail', params: { id: item.song.track.id } }"
            >{{ item.song.track.title }}</b-link>
            <b-link
                v-else
                :to="{ name: 'artist-view', params: { id: item.song.artist.id } }"
            >{{ item.song.artist.title }}</b-link>
          </template>
          <!-- Артист есть, трека нет — название трека другим цветом -->
          <template v-else-if="item.song.artist">
            <b-link
                :to="{ name: 'artist-view', params: { id: item.song.artist.id } }"
                class="font-weight-bold"
            >{{ item.song.artist.title }}</b-link>
            — <span class="text-warning">{{ item.song.title }}</span>
          </template>
          <!-- Артиста нет — имя артиста другим цветом + поиск, как на импорте плейлистов -->
          <template v-else>
            <span class="text-danger">{{ item.song.artist_name }}</span>
            <b-button
                :to="{ name: 'artists-search', query: { q: item.song.artist_name } }"
                variant="info"
                size="sm"
                class="btn-icon ml-25"
                title="Найти артиста"
            >
              <feather-icon icon="SearchIcon"/>
            </b-button>
            <template v-if="item.song.kind !== 'albums'">
              — <span class="text-muted">{{ item.song.title }}</span>
            </template>
          </template>
          <b-badge v-if="item.song.link_type === 'manual'" variant="light-secondary" class="ml-50">вручную</b-badge>
          <b-button
              v-if="$can('manage', 'db-music')"
              variant="flat-primary"
              size="sm"
              class="btn-icon ml-50"
              :title="item.song.track || item.song.artist ? 'Изменить привязку' : 'Привязать'"
              @click="openLink(item.song)"
          >
            <feather-icon icon="LinkIcon"/>
          </b-button>
        </template>
      </b-table>
      <div v-else-if="!isLoading" class="text-muted">Выпусков нет</div>
    </b-overlay>

    <chart-song-history-modal v-model="isHistoryVisible" :song-id="historySongId"/>
    <chart-song-link-modal v-model="isLinkVisible" :song="linkSong" @linked="onLinked"/>
  </b-card>
</template>

<script>
import {
  BCard, BTable, BOverlay, BBadge, BLink, BImg, BButton, BButtonGroup, BFormSelect, BFormRadioGroup,
} from 'bootstrap-vue'
import { computed, onMounted, ref, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import router from '@/router'
import chartsApi from './chartsApi'
import ChartSongHistoryModal from './ChartSongHistoryModal.vue'
import ChartSongLinkModal from './ChartSongLinkModal.vue'

export default {
  components: {
    BCard,
    BTable,
    BOverlay,
    BBadge,
    BLink,
    BImg,
    BButton,
    BButtonGroup,
    BFormSelect,
    BFormRadioGroup,
    ChartSongHistoryModal,
    ChartSongLinkModal,
  },
  setup() {
    const toast = useToast()
    const charts = ref([])
    const chartSlug = ref(router.currentRoute.query.chart || 'uk-singles')
    const year = ref(null)
    const years = ref([])
    const weeks = ref([])
    const edition = ref(null)
    const isLoading = ref(false)

    const isHistoryVisible = ref(false)
    const historySongId = ref(null)
    const isLinkVisible = ref(false)
    const linkSong = ref(null)

    const isAnnual = computed(() => Boolean(edition.value && edition.value.chart.period === 'annual'))

    // У годовых списков премий нет движения/пика/недель; plays — только у чартов TopHit, где он есть
    const fields = computed(() => {
      const chart = edition.value ? edition.value.chart : null
      const hasPlays = Boolean(chart && chart.plays_label && edition.value.entries.some(entry => entry.plays !== null))
      return [
        { key: 'position', label: '#' },
        ...(isAnnual.value ? [] : [{ key: 'move', label: '' }]),
        { key: 'song', label: 'Исполнитель — Название' },
        ...(hasPlays ? [{ key: 'plays', label: chart.plays_label }] : []),
        ...(isAnnual.value ? [] : [{ key: 'peak', label: 'Пик' }, { key: 'weeks', label: 'Недель' }]),
        { key: 'link', label: 'В нашей базе' },
      ]
    })

    const formatNumber = value => Number(value).toLocaleString('ru-RU')

    const chartOptions = computed(() => charts.value.map(chart => ({ text: chart.name, value: chart.slug })))
    const weekOptions = computed(() => weeks.value.map(week => ({ text: week.week_start, value: week.week_start })))

    const selectedWeek = computed({
      get: () => (edition.value ? edition.value.week_start : null),
      set: value => { if (value) goTo(value) }, // eslint-disable-line no-use-before-define
    })

    // Дата и чарт — в query, чтобы ссылкой можно было поделиться
    const syncQuery = () => {
      const query = { chart: chartSlug.value }
      if (edition.value) query.date = edition.value.week_start
      if (JSON.stringify(query) !== JSON.stringify(router.currentRoute.query)) {
        router.replace({ query }).catch(() => {})
      }
    }

    const loadWeeks = async targetYear => {
      const res = await chartsApi.fetchEditions(chartSlug.value, targetYear)
      years.value = res.data.data.years
      weeks.value = res.data.data.editions
      year.value = res.data.data.year
    }

    const loadEdition = async date => {
      isLoading.value = true
      try {
        const res = await chartsApi.fetchEdition(chartSlug.value, date)
        edition.value = res.data.data
        const editionYear = Number(edition.value.week_start.slice(0, 4))
        if (editionYear !== year.value) await loadWeeks(editionYear)
        syncQuery()
      } catch (e) {
        edition.value = null
        // 404 — выпуска для даты/чарта нет, это штатная ситуация («Выпусков нет»);
        // чистим годы/недели, иначе в селектах остаются значения от предыдущего чарта
        if (e.response && e.response.status === 404) {
          years.value = []
          weeks.value = []
        } else {
          toast({
            component: ToastificationContent,
            props: { title: 'Не удалось загрузить выпуск', icon: 'AlertTriangleIcon', variant: 'danger' },
          })
        }
      } finally {
        isLoading.value = false
      }
    }

    const goTo = date => loadEdition(date)

    const openHistory = song => {
      historySongId.value = song.id
      isHistoryVisible.value = true
    }

    const openLink = song => {
      linkSong.value = song
      isLinkVisible.value = true
    }

    // Одна песня может стоять в выпуске один раз — меняем её объект в строке
    const onLinked = song => {
      edition.value.entries.forEach(entry => {
        if (entry.song.id === song.id) entry.song = song
      })
    }

    watch(chartSlug, () => {
      year.value = null
      loadEdition(null)
    })

    watch(year, (value, oldValue) => {
      if (oldValue === null || value === null) return
      if (edition.value && Number(edition.value.week_start.slice(0, 4)) === value) return
      loadWeeks(value).then(() => {
        if (weeks.value.length) loadEdition(weeks.value[0].week_start)
      })
    })

    onMounted(async () => {
      try {
        const res = await chartsApi.fetchCharts()
        charts.value = res.data.data
      } catch (e) {
        toast({
          component: ToastificationContent,
          props: { title: 'Не удалось загрузить список чартов', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
        return
      }
      await loadEdition(router.currentRoute.query.date || null)
    })

    return {
      charts,
      chartSlug,
      chartOptions,
      year,
      years,
      weekOptions,
      selectedWeek,
      edition,
      isLoading,
      fields,
      isAnnual,
      formatNumber,
      goTo,
      isHistoryVisible,
      historySongId,
      openHistory,
      isLinkVisible,
      linkSong,
      openLink,
      onLinked,
    }
  },
}
</script>
